import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from '../../toasts';
import { downloadExcel } from 'react-export-table-to-excel';
import moment from 'moment';
import DebtService from '../../services/debt.service';
import { getError, showCurrency, debtsColumns } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { Section, StatStrip, DataTable, IdChip, Pill, RowActionsMenu, Button } from '../../components/EntityList';

function formatDate(date) {
	return date ? new Date(date).toLocaleDateString('es-UY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function getFieldValue(obj, path) {
	return path.split('.').reduce((value, key) => (value == null ? value : value[key]), obj);
}

function AssetSalesDebts({ rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const { id } = useParams();
	const navigate = useNavigate();
	const [deletingDebt, setDeletingDebt] = useState(null);

	const stats = useMemo(() => {
		const totalByCurrency = new Map();
		rows.forEach((debt) => {
			const key = debt.currency || '—';
			totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (debt.capital || 0));
		});
		return [
			{ label: t('entity.debt.plural'), value: rows.length },
			{
				label: t('field.capital'),
				value: [...totalByCurrency.entries()].map(([currency, total]) => formatMoney(currency, total)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows, t]);

	function newDebtHandler() {
		navigate(`/AdminScreen/assetSales/assetSale/${id}/newDebt`);
	}

	function editDebtHandler(debt) {
		navigate(`/AdminScreen/assetSales/assetSale/${id}/debts/${debt._id}/edit`);
	}

	async function handleConfirmDelete() {
		try {
			await DebtService.delete(deletingDebt._id);
			toast.success(t('msg.debtDeleted'));
			onRefetch();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingDebt(null);
		}
	}

	function handleExport() {
		const exportColumns = debtsColumns.filter((column) => !Object.values(column)[0].forReport);
		const fields = exportColumns.map((column) => Object.keys(column)[0]);
		const header = exportColumns.map((column) => Object.values(column)[0].label);
		const body = rows.map((debt) => fields.map((field) => {
			const value = getFieldValue(debt, field);
			const type = exportColumns.find((column) => Object.keys(column)[0] === field)[field].type;
			if (type === 'date') return formatDate(value);
			return value ?? '';
		}));
		downloadExcel({
			fileName: `${moment.utc().format('DD-MM-YYYY')}-debts`,
			sheet: 'debts',
			tablePayload: { header, body },
		});
	}

	const columns = [
		{
			key: 'customId',
			header: t('field.accountConcept'),
			render: (debt) => (
				<Link to={`/AdminScreen/debts/debt/${debt._id}`} onClick={(e) => e.stopPropagation()}>
					<IdChip>{debt.customId}</IdChip>
				</Link>
			),
		},
		{ key: 'owner', header: t('entity.account.debtor'), render: (debt) => debt.owner?.name },
		{
			key: 'capital',
			header: t('field.capital'),
			className: 'tw:text-right',
			render: (debt) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(debt.currency, debt.capital)}</span>,
		},
		{ key: 'concept', header: t('entity.concept'), render: (debt) => <Pill tone='neutral'>{debt.concept}</Pill> },
		{ key: 'dues', header: t('entity.due.plural'), className: 'tw:font-mono tw:text-right', render: (debt) => debt.dues },
		{
			key: 'amount',
			header: t('field.dueAmount'),
			className: 'tw:text-right',
			render: (debt) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(debt.currency, debt.amount)}</span>,
		},
		{ key: 'date', header: t('field.date'), className: 'tw:font-mono', render: (debt) => formatDate(debt.date) },
		{
			key: 'endDate',
			header: t('field.endDate'),
			className: 'tw:font-mono',
			render: (debt) => (debt.endDate ? formatDate(debt.endDate) : <span className='tw:text-ink-4'>—</span>),
		},
		{ key: 'periodicity', header: t('field.periodicity'), render: (debt) => debt.periodicity },
	];

	const renderRowActions = (debt) => (
		<RowActionsMenu
			items={[
				{ label: t('action.edit'), onClick: () => editDebtHandler(debt) },
				{ label: t('action.delete'), onClick: () => setDeletingDebt(debt), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button size='sm' onClick={handleExport} disabled={rows.length === 0}>
					{t('action.export')}
				</Button>
				<Button size='sm' variant='primary' onClick={newDebtHandler}>
					{t('action.add')} {t('entity.debt')}
				</Button>
			</div>
			<Section title={t('entity.debt.plural')}>
				<DataTable
					columns={columns}
					rows={rows}
					loading={loading}
					error={error}
					onRowClick={(debt) => navigate(`/AdminScreen/debts/debt/${debt._id}`)}
					rowActions={renderRowActions}
					emptyMessage={t('msg.noData')}
				/>
			</Section>

			<ConfirmationModal
				show={Boolean(deletingDebt)}
				handleClose={() => setDeletingDebt(null)}
				handleConfirm={handleConfirmDelete}
				title={t('title.confirmDelete')}
				description={t('msg.confirmDeleteDebtShort')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default AssetSalesDebts;
